/* eslint-disable react/no-array-index-key */
import {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
  Box,
  ClickAwayListener,
  Typography,
  IconButton,
  Fab,
  InputAdornment,
} from '@mui/material';
import { casesApi } from 'src/api/casesApi';
import { alpha } from '@mui/material/styles';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  AutoImage,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  SimpleUploadAdapter,
  TextTransformation,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { userApi } from 'src/api/userApi';
import { reportingApi } from 'src/api/reporting';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { projectModulesApi } from 'src/api/projectModules';
import toast from 'react-hot-toast';

// import { userApi } from 'src/api/userApi';

interface Organization {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  organizationId: string;
}

interface Column {
  id: string;
  label: string;
  placeholder: string;
  type: 'text' | 'autocomplete' | 'multiselect' | 'time' | 'description';
  minWidth?: number;
  options?: any[];
}

interface EditingCell {
  row: number;
  column: string;
}

interface BulkCreateTableProps {
  organizations: Organization[];
  projects: Project[];
  sameOrgForAll: boolean;
  sameProjectForAll: boolean;
  selectedOrg: Organization | null;
  selectedProject: Project | null;
}

interface ValidationErrors {
  [key: number]: {
    [key: string]: string;
  };
}

const columns: Column[] = [
  {
    id: 'organization',
    label: 'Organization',
    placeholder: 'Select organization',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'project',
    label: 'Project',
    placeholder: 'Select project',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'summary',
    label: 'Summary / Title',
    placeholder: 'Enter case title',
    type: 'text',
    minWidth: 200,
  },
  {
    id: 'description',
    label: 'Description',
    placeholder: 'Enter case description',
    type: 'description',
    minWidth: 200,
  },
  {
    id: 'caseType',
    label: 'Case Type',
    placeholder: 'Select case type',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'priority',
    label: 'Priority',
    placeholder: 'Set priority',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'assignee',
    label: 'Assignee',
    placeholder: 'Select assignee',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'label',
    label: 'Label',
    placeholder: 'Add labels',
    type: 'multiselect',
    minWidth: 180,
  },
  {
    id: 'expense',
    label: 'Expense',
    placeholder: 'Select expense',
    type: 'autocomplete',
    minWidth: 100,
    options: [
      { id: 'Opex', name: 'Opex' },
      { id: 'Capex', name: 'Capex' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    placeholder: 'Select account',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'estimatedTime',
    label: 'Estimated Time',
    placeholder: '0h',
    type: 'time',
    minWidth: 80,
  },
  {
    id: 'sprint',
    label: 'Sprint',
    placeholder: 'Select sprint',
    type: 'autocomplete',
    minWidth: 120,
  },
  {
    id: 'fixVersion',
    label: 'Fix Version',
    placeholder: 'Select version',
    type: 'autocomplete',
    minWidth: 120,
  },
  {
    id: 'module',
    label: 'Module',
    placeholder: 'Enter module',
    type: 'autocomplete',
    minWidth: 150,
  },
  {
    id: 'actions',
    label: 'Actions',
    type: 'text',
    minWidth: 50,
    placeholder: '',
  },
];

const BulkCreateTable = forwardRef(
  (
    {
      organizations,
      projects: topLevelProjects,
      sameOrgForAll,
      sameProjectForAll,
      selectedOrg,
      selectedProject,
    }: BulkCreateTableProps,
    ref
  ) => {
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [tableData, setTableData] = useState([
      {
        organization: null,
        project: null,
        summary: '',
        description: '',
        caseType: null,
        priority: null,
        assignee: null,
        label: [],
        expense: null,
        account: null,
        estimatedTime: '',
        sprint: null,
        fixVersion: null,
        module: '',
      },
      {
        organization: null,
        project: null,
        summary: '',
        description: '',
        caseType: null,
        priority: null,
        assignee: null,
        label: [],
        expense: null,
        account: null,
        estimatedTime: '',
        sprint: null,
        fixVersion: null,
        module: '',
      },
      {
        organization: null,
        project: null,
        summary: '',
        description: '',
        caseType: null,
        priority: null,
        assignee: null,
        label: [],
        expense: null,
        account: null,
        estimatedTime: '',
        sprint: null,
        fixVersion: null,
        module: '',
      },
    ]);
    const [rowProjects, setRowProjects] = useState<{
      [key: number]: Project[];
    }>({});
    const [caseTypes, setCaseTypes] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [labelOptions, setLabelOptions] = useState([]);
    const [billingAccounts, setBillingAccounts] = useState<{
      [key: number]: any[];
    }>({});
    const [sprints, setSprints] = useState<{ [key: number]: any[] }>({});
    const [fixVersions, setFixVersions] = useState<{ [key: number]: any[] }>(
      {}
    );
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [deletingRows, setDeletingRows] = useState<number[]>([]);
    const [moduleOptions, setModuleOptions] = useState<string[]>([]);
    const [modulesByRow, setModulesByRow] = useState<{
      [key: number]: string[];
    }>({});
    const [newModule, setNewModule] = useState<boolean>(false);

    useEffect(() => {
      const fetchProjectsForRow = async (rowIndex: number, orgId: string) => {
        try {
          const response = await casesApi.getProjectsById(orgId);
          setRowProjects((prev) => ({
            ...prev,
            [rowIndex]: response.data,
          }));
        } catch (error) {
          console.error('Error fetching projects for row:', error);
          setRowProjects((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
        }
      };

      if (!sameOrgForAll) {
        tableData.forEach((row, index) => {
          if (row.organization?.id) {
            fetchProjectsForRow(index, row.organization.id);
          }
        });
      }
    }, [
      JSON.stringify(tableData.map((row) => row.organization)),
      sameOrgForAll,
    ]);

    useEffect(() => {
      if (sameOrgForAll && selectedOrg) {
        setTableData((prevData) =>
          prevData.map((row) => {
            if (row.organization?.id !== selectedOrg.id) {
              return {
                ...row,
                organization: selectedOrg,
                project: null,
              };
            }
            return row;
          })
        );
      }
    }, [selectedOrg]);

    useEffect(() => {
      if (sameOrgForAll && selectedOrg) {
        setTableData((prevData) =>
          prevData.map((row) => {
            if (row.organization?.id !== selectedOrg.id) {
              return {
                ...row,
                organization: selectedOrg,
                project: null,
              };
            }
            return row;
          })
        );
      }
    }, [sameOrgForAll]);

    useEffect(() => {
      if (sameProjectForAll) {
        setTableData((prevData) =>
          prevData.map((row) => ({
            ...row,
            project: selectedProject,
          }))
        );
      }
    }, [sameProjectForAll, selectedProject]);

    useEffect(() => {
      const fetchCaseTypes = async () => {
        try {
          const response = await casesApi.getCaseTypes();
          setCaseTypes(response?.data);
        } catch (error) {
          console.error('Error fetching case types:', error);
          setCaseTypes([]);
        }
      };
      fetchCaseTypes();
    }, []);

    useEffect(() => {
      const fetchPriorities = async () => {
        try {
          const response = await casesApi.getPriorities();
          setPriorities(response?.data);
        } catch (error) {
          console.error('Error fetching priorities:', error);
          setPriorities([]);
        }
      };
      fetchPriorities();
    }, []);

    useEffect(() => {
      const fetchEmployees = async () => {
        try {
          const response = await casesApi.getAutoCompleteUser(false);
          setEmployees(response?.data);
        } catch (error) {
          console.error('Error fetching employees:', error);
          setEmployees([]);
        }
      };
      fetchEmployees();
    }, []);

    useEffect(() => {
      const fetchLabels = async () => {
        try {
          const response = await casesApi.getAutoCompleteLabels();
          const uniqueLabels = response?.data?.filter(
            (value, index, self) => self.indexOf(value) === index
          );
          setLabelOptions(uniqueLabels);
        } catch (error) {
          console.error('Error fetching labels:', error);
          setLabelOptions([]);
        }
      };
      fetchLabels();
    }, []);

    useEffect(() => {
      const fetchBillingAccounts = async (
        rowIndex: number,
        projectId: string
      ) => {
        if (!projectId) {
          setBillingAccounts((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
          return;
        }

        try {
          const response = await reportingApi.getBillingAccountsByProjectId({
            projectId,
          });
          setBillingAccounts((prev) => ({
            ...prev,
            [rowIndex]: response?.data || [],
          }));
        } catch (error) {
          console.error('Error fetching billing accounts:', error);
          setBillingAccounts((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
        }
      };

      tableData.forEach((row, index) => {
        if (row.project?.id) {
          fetchBillingAccounts(index, row.project.id);
        }
      });
    }, [JSON.stringify(tableData.map((row) => row.project?.id))]);

    useEffect(() => {
      const fetchSprints = async (rowIndex: number, projectId: string) => {
        if (!projectId) {
          setSprints((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
          return;
        }

        try {
          const response = await casesApi.getAutoCompleteSprints(projectId);
          setSprints((prev) => ({
            ...prev,
            [rowIndex]: response?.data?.active || [],
          }));
        } catch (error) {
          console.error('Error fetching sprints:', error);
          setSprints((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
        }
      };

      tableData.forEach((row, index) => {
        if (row.project?.id) {
          fetchSprints(index, row.project.id);
        }
      });
    }, [JSON.stringify(tableData.map((row) => row.project?.id))]);

    useEffect(() => {
      const fetchFixVersions = async (rowIndex: number, projectId: string) => {
        if (!projectId) {
          setFixVersions((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
          return;
        }

        try {
          const response = await casesApi.getAutoCompleteReleaseVersion(
            projectId
          );
          setFixVersions((prev) => ({
            ...prev,
            [rowIndex]: response?.data?.active || [],
          }));
        } catch (error) {
          console.error('Error fetching fix versions:', error);
          setFixVersions((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
        }
      };

      tableData.forEach((row, index) => {
        if (row.project?.id) {
          fetchFixVersions(index, row.project.id);
        }
      });
    }, [JSON.stringify(tableData.map((row) => row.project?.id))]);




    useEffect(() => {
      const fetchModulesForRow = async (
        rowIndex: number,
        orgId: string,
        projectId: string
      ) => {
        if (!orgId || !projectId) {
          setModulesByRow((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
          return;
        }

        try {
          const response = await projectModulesApi.getModulesByOrgAndProject(
            orgId,
            projectId
          );
          const moduleNames =
            response?.data?.map((module) => module.name) || [];
          setModulesByRow((prev) => ({
            ...prev,
            [rowIndex]: moduleNames,
          }));
        } catch (error) {
          console.error('Error fetching modules:', error);
          setModulesByRow((prev) => ({
            ...prev,
            [rowIndex]: [],
          }));
        }
      };

      tableData.forEach((row, index) => {
        if (row.organization?.id && row.project?.id) {
          fetchModulesForRow(index, row.organization.id, row.project.id);
        }
      });
    }, [
      JSON.stringify(
        tableData.map((row) => ({
          orgId: row.organization?.id,
          projectId: row.project?.id,
        }))
      ),
      newModule
    ]);

    function uploadAdapter(loader) {
      return {
        upload: () =>
          new Promise((resolve) => {
            loader.file.then(async (file) => {
              const formData = new FormData();
              formData.append('file', file);
              const image = await userApi.uploadAttachments(formData);
              resolve({
                default: `${image?.data?.blob_url}`,
              });
            });
          }),
      };
    }
    function uploadPlugin(editor) {
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
        uploadAdapter(loader);
    }
    const editorConfig = {
      toolbar: {
        items: [
          '|',
          'heading',
          '|',
          'bold',
          'italic',
          '|',
          'link',
          'blockQuote',
          'insertImage',
          '|',
          'bulletedList',
          'numberedList',
        ],
        shouldNotGroupWhenFull: false,
      },
      plugins: [
        AutoImage,
        BlockQuote,
        Bold,
        Essentials,
        Heading,
        ImageBlock,
        ImageCaption,
        ImageInline,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        Link,
        List,
        ListProperties,
        Paragraph,
        SimpleUploadAdapter,
        TextTransformation,
      ],
      blockToolbar: [
        'bold',
        'italic',
        '|',
        'link',
        '|',
        'bulletedList',
        'numberedList',
        'outdent',
        'indent',
      ],
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
        ],
      },
      image: {
        toolbar: [
          'toggleImageCaption',
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:wrapText',
          'imageStyle:breakText',
          '|',
          'resizeImage',
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file',
            },
          },
        },
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      extraPlugins: [uploadPlugin],
      placeholder: 'Enter your description here',
    };

    const handleCellClick = (row: number, column: string) => {
      setEditingCell({ row, column });
    };

    const handleValueChange = (
      rowIndex: number,
      columnId: string,
      newValue: any
    ) => {
      setTableData((prevData) => {
        const newData = [...prevData];
        newData[rowIndex] = {
          ...newData[rowIndex],
          [columnId]: newValue,
        };

        // Clear error for this field when value changes
        setErrors((prev) => ({
          ...prev,
          [rowIndex]: {
            ...prev[rowIndex],
            [columnId]: undefined,
          },
        }));

        return newData;
      });
    };

    const handleClickAway = useCallback(() => {
      setEditingCell(null);
    }, []);

    const handleAutocompleteChange = (
      rowIndex: number,
      column: Column,
      newValue: any
    ) => {
      if (column.id === 'organization') {
        setTableData((prevData) => {
          const newData = [...prevData];
          newData[rowIndex] = {
            ...newData[rowIndex],
            organization: newValue,
            project: null, // Reset project when organization changes
            account: null, // Reset account when organization changes
            sprint: null, // Reset sprint when organization changes
            fixVersion: null, // Reset fixVersion when organization changes
            module: '',
          };
          return newData;
        });
      } else if (column.id === 'project') {
        setTableData((prevData) => {
          const newData = [...prevData];
          newData[rowIndex] = {
            ...newData[rowIndex],
            project: newValue,
            account: null, // Reset account when project changes
            sprint: null, // Reset sprint when project changes
            fixVersion: null, // Reset fixVersion when project changes
            module: '',
          };
          return newData;
        });
      } else {
        handleValueChange(rowIndex, column.id, newValue);
      }

      setTimeout(() => {
        setEditingCell(null);
      }, 0);
    };
    const getOptions = (columnId: string, rowIndex: number) => {
      switch (columnId) {
        case 'organization':
          return organizations;
        case 'project': {
          if (sameOrgForAll) {
            return topLevelProjects;
          }
          return rowProjects[rowIndex] || [];
        }
        case 'expense':
          return [
            { id: 'Opex', name: 'Opex' },
            { id: 'Capex', name: 'Capex' },
          ];
        case 'priority':
          return priorities;
        case 'caseType':
          return caseTypes;
        case 'assignee':
          return employees;
        case 'label':
          return labelOptions;
        case 'account':
          return billingAccounts[rowIndex] || [];
        case 'sprint':
          return sprints[rowIndex] || [];
        case 'fixVersion':
          return fixVersions[rowIndex] || [];
        case 'module':
          return modulesByRow[rowIndex] || [];
        default: {
          const column = columns.find((col) => col.id === columnId);
          return column?.options || [];
        }
      }
    };

    const getDisplayValue = (column: Column, value: any, rowIndex: number) => {
      const error = errors[rowIndex]?.[column.id];

      if (!value) {
        return (
          <Typography
            sx={{
              color: error ? 'error.main' : 'text.secondary',
              fontSize: '0.875rem',
            }}>
            {column.placeholder}
          </Typography>
        );
      }

      const isValidTimeFormat = /^(\d+h)?(\s*\d+m)?$/.test(
        value?.trim?.() || ''
      );

      switch (column.type) {
        case 'autocomplete':
          if (column.id === 'module') {
            return value;
          }
          return (
            value.name || (
              <Typography
                sx={{
                  color: error ? 'error.main' : 'text.secondary',
                  fontSize: '0.875rem',
                }}>
                {column.placeholder}
              </Typography>
            )
          );
        case 'multiselect':
          return value.length > 0 ? (
            value.join(', ')
          ) : (
            <Typography
              sx={{
                color: error ? 'error.main' : 'text.secondary',
                fontSize: '0.875rem',
              }}>
              {column.placeholder}
            </Typography>
          );
        case 'description':
          return (
            <div
              className="description-content"
              dangerouslySetInnerHTML={{
                __html:
                  value ||
                  `<span style="color: ${error ? '#d32f2f' : '#696969'}">${column.placeholder
                  }</span>`,
              }}
            />
          );
        case 'time':
          return (
            <Typography
              sx={{
                color: isValidTimeFormat ? 'text.primary' : 'error.main',
                fontSize: '0.875rem',
              }}>
              {value}
            </Typography>
          );
        default:
          return (
            <Typography
              sx={{
                color: error ? 'error.main' : 'text.primary',
                fontSize: '0.875rem',
              }}>
              {value}
            </Typography>
          );
      }
    };

    const validateRow = (rowData: any, rowIndex: number) => {
      const rowErrors: { [key: string]: string } = {};

      // Required field validations
      if (!rowData.organization) {
        rowErrors.organization = 'Organization is required';
      }
      if (!rowData.project) {
        rowErrors.project = 'Project is required';
      }
      if (!rowData.summary?.trim()) {
        rowErrors.summary = 'Summary is required';
      }
      if (!rowData.description?.trim()) {
        rowErrors.description = 'Description is required';
      }
      if (!rowData.caseType) {
        rowErrors.caseType = 'Case Type is required';
      }
      if (!rowData.priority) {
        rowErrors.priority = 'Priority is required';
      }
      if (!rowData.assignee) {
        rowErrors.assignee = 'Assignee is required';
      }
      if (rowData.label?.length === 0) {
        rowErrors.label = 'Label is required';
      }
      if (!rowData.expense) {
        rowErrors.expense = 'Expense is required';
      }
      if (!rowData.account) {
        rowErrors.account = 'Account is required';
      }
      if (!rowData.estimatedTime) {
        rowErrors.estimatedTime = 'Estimated Time is required';
      } else {
        // Check if the format matches either "Xh" or "Xm" or "Xh Ym"
        const isValidFormat = /^(\d+h)?(\s*\d+m)?$/.test(
          rowData.estimatedTime.trim()
        );
        if (!isValidFormat) {
          rowErrors.estimatedTime =
            'Invalid format. Use format: 1h, 30m, or 1h 30m';
        }
      }

      // Format validation for estimated time
      if (rowData.estimatedTime && !/^\d+h$/.test(rowData.estimatedTime)) {
        rowErrors.estimatedTime = 'Invalid format. Use format: 1h, 2h, etc.';
      }

      // Format validation for estimated time
      if (rowData.estimatedTime && !/^\d+h$/.test(rowData.estimatedTime)) {
        rowErrors.estimatedTime = 'Invalid format. Use format: 1h, 2h, etc.';
      }

      if (!rowData.module?.trim()) {
        rowErrors.module = 'Module is required';
      }

      // Update errors state for this row
      setErrors((prev) => ({
        ...prev,
        [rowIndex]: rowErrors,
      }));

      return Object.keys(rowErrors).length === 0;
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      validateAllRows: () => {
        let isValid = true;
        tableData.forEach((rowData, index) => {
          if (!validateRow(rowData, index)) {
            isValid = false;
          }
        });
        return isValid;
      },
      getTableData: () => tableData,
      addRow: () => {
        setTableData((prevData) => [
          ...prevData,
          {
            organization: sameOrgForAll ? selectedOrg : null,
            project: sameProjectForAll ? selectedProject : null,
            summary: '',
            description: '',
            caseType: null,
            priority: null,
            assignee: null,
            label: [],
            expense: null,
            account: null,
            estimatedTime: '',
            sprint: null,
            fixVersion: null,
            module: '',
          },
        ]);
      },
    }));

    const handleDeleteClick = (rowIndex: number) => {
      setDeletingRows((prev) => [...prev, rowIndex]);
    };

    const handleConfirmDelete = (rowIndex: number) => {
      setTableData((prevData) => {
        if (prevData.length <= 1) return prevData; // Prevent removing last row
        return prevData.filter((_, index) => index !== rowIndex);
      });
      setDeletingRows((prev) => prev.filter((index) => index !== rowIndex));
    };

    const handleCancelDelete = (rowIndex: number) => {
      setDeletingRows((prev) => prev.filter((index) => index !== rowIndex));
    };

    const renderCell = (rowIndex: number, column: Column, value: any) => {
      const isEditing =
        editingCell?.row === rowIndex && editingCell?.column === column.id;
      const isSummaryEditing =
        editingCell?.row === rowIndex && editingCell?.column === 'summary';
      const error = errors[rowIndex]?.[column.id];

      // Add error styling to the cell components
      const commonStyles = {
        ...(error && {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'error.main',
            },
            '&:hover fieldset': {
              borderColor: 'error.main',
            },
          },
        }),
      };

      const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          handleClickAway();
        }
      };

      if (column.id !== 'summary' && isSummaryEditing) {
        return null;
      }

      switch (column.type) {
        case 'text':
          return (
            <TextField
              size="small"
              fullWidth
              multiline={column.id === 'description'}
              maxRows={column.id === 'description' ? 3 : 1}
              value={value}
              onChange={(e) =>
                handleValueChange(rowIndex, column.id, e.target.value)
              }
              onBlur={handleClickAway}
              onKeyDown={handleKeyPress}
              placeholder={column.placeholder}
              autoFocus
              error={!!error}
              helperText={error}
              label={column.label}
              sx={{
                minWidth: column.minWidth,
                ...commonStyles,
                ...(column.id === 'summary' && {
                  position: 'absolute',
                  width: 'auto',
                  left: (theme) => `${theme.spacing(2)}`,
                  right: (theme) => `${theme.spacing(2)}`,
                  zIndex: 1,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.background.paper, 0.95),
                  animation: isEditing ? 'fadeIn 0.3s ease-in-out' : 'none',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'scale(0.95)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }),
              }}
            />
          );
        case 'autocomplete':
          if (column.id === 'module') {
            const row = tableData[rowIndex];
            const isDisabled = !row?.organization?.id || !row?.project?.id;

            return (
              <Autocomplete
                size="small"
                options={getOptions(column?.id, rowIndex)}
                value={
                  value?.startsWith(`${row?.project?.name} - `)
                    ? value.replace(`${row?.project?.name} - `, '')
                    : value || ''
                }
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    const prefixedValue = newValue.startsWith(`${row.project.name} - `)
                      ? newValue
                      : `${row.project.name} - ${newValue}`;

                    handleValueChange(rowIndex, column.id, prefixedValue);

                    try {
                      projectModulesApi.createModule({
                        orgId: row?.organization?.id,
                        projectId: row?.project?.id,
                        name: prefixedValue,
                      });
                      setNewModule((prev) => !prev);
                    } catch (err) {
                      toast.error(err.message ?? 'Error while adding module');
                    }
                  }
                }}
                filterOptions={(options, params) => {
                  const { inputValue } = params;
                  const normalizedInput = inputValue.toLowerCase();

                  const filtered = options.filter((option) =>
                    option.toLowerCase().includes(normalizedInput)
                  );

                  const isExisting = options.some(
                    (option) => option.toLowerCase() === normalizedInput
                  );

                  if (inputValue && !isExisting) {
                    filtered.push(inputValue);
                  }

                  return Array.from(new Set(filtered));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label={column.label}
                    placeholder={
                      isDisabled
                        ? 'Select organization and project first'
                        : column.placeholder
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: row?.project?.id && (
                        <InputAdornment position="start">
                          <Typography sx={{ color: '#666', fontSize: '14px' }}>
                            {`${row?.project?.name} -`}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    error={!!error}
                    helperText={error}
                    sx={commonStyles}
                  />
                )}
                sx={{ minWidth: column.minWidth }}
              />

            );
          }
          return (
            <Autocomplete
              size="small"
              options={getOptions(column.id, rowIndex)}
              getOptionLabel={(option) => option?.name || ''}
              value={value}
              onChange={(_, newValue) =>
                handleAutocompleteChange(rowIndex, column, newValue)
              }
              onBlur={handleClickAway}
              blurOnSelect
              disabled={
                (column.id === 'organization' && sameOrgForAll) ||
                (column.id === 'project' && sameProjectForAll)
              }
              autoFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label={column.label}
                  placeholder={column.placeholder}
                  error={!!error}
                  helperText={error}
                  sx={commonStyles}
                />
              )}
              sx={{ minWidth: column.minWidth }}
            />
          );

        case 'multiselect':
          return (
            <Autocomplete
              size="small"
              multiple
              options={getOptions(column.id, rowIndex)}
              value={value}
              onChange={(_, newValue) =>
                handleAutocompleteChange(rowIndex, column, newValue)
              }
              onBlur={handleClickAway}
              blurOnSelect
              autoFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label={column.label}
                  placeholder={column.placeholder}
                  error={!!error}
                  helperText={error}
                  sx={commonStyles}
                />
              )}
              sx={{ minWidth: column.minWidth }}
            />
          );

        case 'time':
          return (
            <TextField
              size="small"
              value={value}
              onChange={(e) =>
                handleValueChange(rowIndex, column.id, e.target.value)
              }
              onBlur={handleClickAway}
              placeholder={column.placeholder}
              autoFocus
              label={column.label}
              error={!!error}
              helperText={error}
              sx={{ minWidth: column.minWidth, ...commonStyles }}
            />
          );

        case 'description':
          return (
            <Box
              sx={{
                border: (theme) =>
                  error ? `1px solid ${theme.palette.error.main}` : 'none',
                borderRadius: 1,
                position: 'relative',
              }}>
              {error && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    bottom: -20,
                    left: 0,
                  }}>
                  {error}
                </Typography>
              )}
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                  sx={{
                    cursor: 'auto',
                    position: 'absolute',
                    top: -30,
                    left: -8,
                    width: '400px',
                    backgroundColor: 'white',
                    zIndex: 100,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '4px',
                    // ...(window.innerWidth - 400 < 0 && {
                    //   right: 0,
                    //   left: 'auto',
                    // }),
                  }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={value || ''}
                    config={editorConfig as any}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      handleValueChange(rowIndex, column.id, data);
                    }}
                    onReady={(editor) => {
                      editor.editing.view.focus();
                      if (value) {
                        editor.setData(value);
                      }
                    }}
                  />
                </Box>
              </ClickAwayListener>
            </Box>
          );

        default:
          return (
            <TextField
              size="small"
              fullWidth
              multiline={column.id === 'description'}
              maxRows={column.id === 'description' ? 3 : 1}
              value={value}
              onChange={(e) =>
                handleValueChange(rowIndex, column.id, e.target.value)
              }
              onBlur={handleClickAway}
              placeholder={column.placeholder}
              autoFocus
              error={!!error}
              helperText={error}
              sx={{ minWidth: column.minWidth, ...commonStyles }}
            />
          );
      }
    };

    return (
      <Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: '500px',
            mt: 2,
            '&::-webkit-scrollbar': {
              height: '6px', // Reduce scrollbar height
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.15)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.25)',
              },
            },
          }}>
          <Table size="small" aria-label="bulk create cases table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id} sx={{ minWidth: column.minWidth }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column) => {
                    const isEditing =
                      editingCell?.row === rowIndex &&
                      editingCell?.column === column.id;
                    const isSummaryEditing =
                      editingCell?.row === rowIndex &&
                      editingCell?.column === 'summary';

                    if (column.id === 'actions') {
                      return (
                        <TableCell key={`${rowIndex}-${column.id}`}>
                          {deletingRows.includes(rowIndex) ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConfirmDelete(rowIndex);
                                }}
                                sx={{ color: 'success.main' }}
                                disabled={tableData.length <= 1}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelDelete(rowIndex);
                                }}
                                sx={{ color: 'error.main' }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(rowIndex);
                              }}
                              disabled={tableData.length <= 1}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={`${rowIndex}-${column.id}`}
                        onClick={() => handleCellClick(rowIndex, column.id)}
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          minWidth: column.minWidth,
                          transition: 'opacity 0.3s ease-in-out',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          ...(isSummaryEditing &&
                            column.id !== 'summary' && {
                            opacity: 0.3,
                            pointerEvents: 'none',
                          }),
                          ...(column.id === 'summary' &&
                            isEditing && {
                            position: 'absolute',
                            left: (theme) => `${theme.spacing(3)}`,
                            right: (theme) => `${theme.spacing(3)}`,
                            width: 'auto',
                            zIndex: 1,
                            backgroundColor: (theme) =>
                              alpha(theme.palette.background.paper, 0.95),
                          }),
                          ...(column.type === 'description' && {
                            maxWidth: '300px',
                            '& .description-content': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: '1.5em',
                              maxHeight: '3em',
                              '& *': {
                                margin: 0,
                                padding: 0,
                                display: 'inline',
                              },
                              '& p': {
                                display: 'inline',
                              },
                              '& ul, & ol': {
                                display: 'inline',
                                listStylePosition: 'inside',
                              },
                              '& li': {
                                display: 'inline',
                              },
                            },
                          }),
                        }}>
                        {editingCell?.row === rowIndex &&
                          editingCell?.column === column.id
                          ? renderCell(rowIndex, column, row[column.id])
                          : getDisplayValue(column, row[column.id], rowIndex)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
);

export default BulkCreateTable;
